import React, { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import PropTypes from 'prop-types'
import { ExclamationCircleIcon, XIcon } from '@heroicons/react/outline'
import SuccessMessageSvg from '../../svg/SuccessMessage'
import clsx from 'clsx'
import { isNil } from 'ramda'

const AlertModal = ({
    title,
    message,
    onDone,
    onCancel,
    textOk,
    textCancel,
    type = 'success',
    isOpen,
    setIsOpen,
}) => {
    const renderIcon = () => {
        switch (type) {
            case 'success':
                return <SuccessMessageSvg className="w-auto m-auto" />
            case 'error':
                return (
                    <ExclamationCircleIcon className="w-12 h-12 m-auto text-textDarkGray" />
                )
            default:
                return null
        }
    }

    const closeModal = () => {
        setIsOpen(false)
    }

    return (
        <>
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-200"
                    onClose={closeModal}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-modalBackground bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-white md:p-12 p-6 text-center transition-all relative">
                                    <XIcon
                                        className="w-4 h-4 stroke-current stroke-2 absolute text-textDarkerGray top-6 right-6 cursor-pointer"
                                        onClick={closeModal}
                                    />
                                    {renderIcon()}

                                    <Dialog.Title
                                        as="h3"
                                        className="mt-6 text-lg text-textDarkerGray font-semibold"
                                    >
                                        {title}
                                    </Dialog.Title>
                                    <p className="mt-1 text-textDarkGray text-sm font-normal text-center">
                                        {message}
                                    </p>

                                    {(!isNil(textOk) || !isNil(textCancel)) && (
                                        <div className="mt-6 flex  mobile:flex-row flex-col items-center justify-between gap-4">
                                            {!isNil(textCancel) && (
                                                <button
                                                    type="button"
                                                    className="outline-none flex-grow w-full h-auto text-sm font-medium text-black p-3 rounded-xl border border-solid border-lineGray"
                                                    onClick={() => {
                                                        onCancel && onCancel()
                                                        closeModal()
                                                    }}
                                                >
                                                    {textCancel}
                                                </button>
                                            )}

                                            {!isNil(textOk) && (
                                                <button
                                                    type="button"
                                                    className={clsx(
                                                        'flex-grow w-full outline-none h-auto text-sm font-medium text-white bg-black p-3 rounded-xl border border-solid border-blueDarker',
                                                        type === 'error' &&
                                                            'border-colorsStatusBadges-on-hold-text bg-colorsStatusBadges-on-hold-text',
                                                    )}
                                                    onClick={onDone}
                                                >
                                                    {textOk}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    )
}

AlertModal.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    message: PropTypes.string,
    onDone: PropTypes.func,
    onCancel: PropTypes.func,
    textOk: PropTypes.string,
    textCancel: PropTypes.string,
    type: PropTypes.string,
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
}

export default AlertModal
